import React, { Fragment, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useTheme from '@mui/styles/useTheme';
import NavigationBar from 'apollo-react/components/NavigationBar';
import Typography from 'apollo-react/components/Typography';
import Box from 'apollo-react/components/Box';
import User from 'apollo-react-icons/User';
import Globe from 'apollo-react-icons/Globe';
import IQVIALogo from "../../../assets/images/IQVIA-logo.png";
import BackgroundWaves from "../../../assets/images/background-waves.png";
import io from "socket.io-client";
import axios from 'axios';

const Header = (props) => {
  const { idsTokens } = useTheme();
  const [variant] = useState('accent');
  const [hideProfile] = useState(false);
  const { t, i18n } = useTranslation();

  // Initially start with an empty notifications list.
  const [notifications, setNotifications] = useState([]);
  const userId  = props?.profileMenuProps?.id;
  
  const onClick = (id) =>
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id
          ? { ...notification, unread: false }
          : notification
      )
    );
    
  // Fetch notifications from the API endpoint and map them to the desired structure.
  // Subscribe to notifications via Socket.io when the user is logged in.
  useEffect(() => {
    if (!userId) return;
    if (props?.profileMenuProps?.persona) {
      axios.get(`${process.env.REACT_APP_SERVER_URL}/api/${props?.profileMenuProps?.persona}/notifications/${userId}`, { withCredentials: true })
        .then((response) => {
          // Transform fetched notifications into our desired structure.
          const mappedNotifications = response.data.map(n => ({
            id: n?.id,
            header: n.action || 'Header',
            details: n.event || 'Event details currently not available.',
            unread: n.readAt === null,
            timestamp: n.createdAt,
          }));
          setNotifications(mappedNotifications);
        })
        .catch((error) => {
          console.error("Error fetching notifications:", error);
        });
    }
    const socket = io(process.env.REACT_APP_SERVER_URL, {
      query: { userId , type: props?.profileMenuProps?.persona },
      withCredentials: true,
    });
    socket.on("notification", (data) => {
      const newNotification = {
        id: data?.id,
        header: data.action || 'Header',
        details:
          data.event ||
          'Event details currently not available.',
        unread: true,
        timestamp: data.createdAt,
      };
      setNotifications((prev) => [newNotification, ...prev]);
    });
    socket.on("reminder", (data) => {
      console.log("Received reminder notification:", data);
      const newNotification = {
        id: data?.id,
        header: data.action || 'Header',
        details: data.event || 'Event details currently not available.',
        unread: true,
        timestamp: data.createdAt,
      };
      setNotifications((prev) => [newNotification, ...prev]);
    });

    // Clean up the socket connection when component unmounts.
    return () => socket.disconnect();
  }, [userId]);

  // Function to toggle language.
  const toggleLanguage = () => {
    const currentLang = i18n.language;
    const newLang = currentLang === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleNotificationClick = (id) => {
    if (props?.profileMenuProps?.persona) {
    axios.put(`${process.env.REACT_APP_SERVER_URL}/api/${props?.profileMenuProps?.persona}/notifications/${id}/open`, {}, { withCredentials: true })
      .then(() => {
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === id ? { ...notification, unread: false } : notification
          )
        );
      })
      .catch((error) => {
        console.error("Error marking notification as read:", error.response?.data || error.message);
      });
  }
}
  const handleDeleteNotification = (id) => {
    if (props?.profileMenuProps?.persona) {
    axios.delete(`${process.env.REACT_APP_SERVER_URL}/api/${props?.profileMenuProps?.persona}/notifications/${id}`, { withCredentials: true })
      .then(() => {
        setNotifications((prev) => prev.filter(notification => notification.id !== id));
      })
      .catch((error) => {
        console.error("Error deleting notification:", error.response?.data || error.message);
      });
  }
}
  
  const profileMenuProps = props.profileMenuProps ? {
    ...props.profileMenuProps,
    logoutText: t('sign_out'),
    logoutButtonProps: { onClick: () => props.profileMenuProps.logout() },
    extraContent: (
      <Typography
        component="p"
        variant="caption"
        align="center"
        color={idsTokens.listItem.default.color.text.sub.enabled}
        sx={{ marginBottom: '8px' }}
      >
        v1.0.1 April 22, 2025
      </Typography>
    ),
    menuItems: [
      // {
      //   text: 'Profile',
      //   icon: User,
      //   onClick: () => console.log('Profile clicked'),
      // },
      {
        text: i18n.language === 'en' ? 'عربي' : 'English',
        icon: Globe,
        onClick: () => {
          toggleLanguage();
        }
      },
    ],
  } : undefined;

  return (
    <>
      <NavigationBar
        style={{
          background: "url(/static/media/waves.a2a40f0467a566181534cfa81efbbb4d.svg),linear-gradient(to right, #250056, #0076AE), url(../../../assets/images/background-waves.png)",
          backgroundRepeat: "no-repeat",
          height: '56px',
          justifyContent: 'space-between'
        }}
        LogoComponent={() => (
          <Fragment>
            <img src={IQVIALogo}
              width="105.54px" height="18.91px"
              alt="IQVIA" style={{ marginRight: 24 }} />
            <Typography
              style={{
                color: idsTokens.navbar[variant].color.content.item.enabled,
                lineHeight: '56px',
                marginRight: 24,
                zIndex: 2,
                whiteSpace: 'nowrap',
              }}
            >
              Conveyor
            </Typography>
          </Fragment>
        )}
        profileMenuProps={props.profileMenuProps
          ? { ...profileMenuProps, name: hideProfile ? '' : profileMenuProps.name }
          : undefined}
        maxWidth={'100%'}
        notificationsMenuProps={{
          notificationIdField: 'id',
          notifications,
          onToggleRead: handleNotificationClick,
          onDelete:handleDeleteNotification,
          onClick: handleNotificationClick,
          onButtonClick: () => { },
          emptyText: (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="bodyDefault" emphasis="high" color="#333">
                {'No notifications right now!'}
              </Typography>
              <Typography variant="bodySmall" color="#333">
                {'All squeaky clean.'}
              </Typography>
            </Box>
          ),
        }}
      />
      <img src={BackgroundWaves}
        width="226px" height="29px"
        alt=""
        style={{ position: 'fixed', top: "56px", right: 0 }}
      />
    </>
  );
};

export default Header;
